import React, { useEffect, useRef, useState } from 'react';
import './CursorTracker.css';

interface CursorTrackerProps {
  children: React.ReactNode;
}

const CursorTracker: React.FC<CursorTrackerProps> = ({ children }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const supportsFineHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!supportsFineHover) {
      setIsHovering(false);
      return;
    }

    let animationFrameId: number;
    let lastTime = 0;
    const throttleTime = 16; // ~60fps

    const handleMouseMove = (e: MouseEvent) => {
      const currentTime = Date.now();
      if (currentTime - lastTime < throttleTime) return;
      
      lastTime = currentTime;
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      animationFrameId = requestAnimationFrame(() => {
        setMousePosition({ x, y });
        setIsHovering(true);
      });
    };

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    // Add global event listeners
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('mouseleave', handleMouseLeave);

    // Set initial state
    setIsHovering(true);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('mouseleave', handleMouseLeave);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className={`cursor-tracker ${isHovering ? 'cursor-active' : ''}`}
      style={{
        '--mouse-x': `${mousePosition.x}%`,
        '--mouse-y': `${mousePosition.y}%`,
      } as React.CSSProperties}
    >
      <div className="cursor-background" />
      <div className="cursor-particles" />
      {children}
    </div>
  );
};

export default CursorTracker;